// The Doc Pabean / Monitoring Blokir "Download Report" dialog (revamp PR 7a). Same requests (drafters once per open,
// companies on open and on every keystroke of the company search, the export or its demo twin), filters, date rules
// and file names as the Mantine modal it replaces; the dialog, fields and toasts are Lumen and sonner.
import dayjs from 'dayjs';
import { Check, ChevronDown, Download, Loader2, X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';

import { DateField } from '@/components/form/date-field';
import { Button } from '@/components/lumen/button';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/lumen/command';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/lumen/dialog';
import { controlClasses } from '@/components/lumen/input';
import { Label } from '@/components/lumen/label';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/lumen/popover';
import { cn } from '@/lib/utils';
import { formatTemplate } from '@/lib/format-template';
import { useLanguage } from '@contexts/LanguageContext';
import usePPJK from '@hooks/use-ppjk';
import axiosInstance from '@libs/axios';

const DATE_REQUIRED_COMPANY = 'PT ASTRA VISTEON INDONESIA';

interface DrafterOption {
  value: string;
  label: string;
}

interface CompanyOption {
  value: string;
  label: string;
}

interface DownloadReportModalProps {
  opened: boolean;
  onClose: () => void;
  /**
   * Branch selection to export, mirroring the listing's own filter: '' means
   * the caller's own branch, BRANCH_ALL means every branch. Without this the
   * export would always fall back to the caller's own branch, so an audit user
   * who had picked "Semua Cabang" on screen would silently download a narrower
   * workbook than the one they were looking at.
   */
  branchFilter?: string;
}

export default function DownloadReportModal({ opened, onClose, branchFilter = '' }: DownloadReportModalProps) {
  const { t } = useLanguage();
  const { nomorPpjk, isLoading: ppjkLoading } = usePPJK();
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [drafterOptions, setDrafterOptions] = useState<DrafterOption[]>([]);
  const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([]);
  const [selectedDrafter, setSelectedDrafter] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [companySearchValue, setCompanySearchValue] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const requiresDateRange = selectedCompany === DATE_REQUIRED_COMPANY;

  // Check if current user is a demo user
  const isDemoUser = () => {
    try {
      const authData = localStorage.getItem('authData');
      if (authData) {
        const parsedAuth = JSON.parse(authData);
        return parsedAuth.email?.toLowerCase().includes('demo');
      }
    } catch (error) {
      console.warn('Failed to parse authData:', error);
    }
    return false;
  };

  // Dummy data for demo users
  const dummyDrafterOptions: DrafterOption[] = [
    { value: 'John Doe', label: 'John Doe' },
    { value: 'Jane Smith', label: 'Jane Smith' },
    { value: 'Bob Wilson', label: 'Bob Wilson' },
    { value: 'Alice Johnson', label: 'Alice Johnson' },
    { value: 'Mike Brown', label: 'Mike Brown' },
  ];

  const dummyCompanyOptions: CompanyOption[] = [
    { value: 'PT DEMO INDONESIA', label: 'PT DEMO INDONESIA' },
    { value: 'PT SAMPLE TRADING', label: 'PT SAMPLE TRADING' },
    { value: 'CV DEMO JAYA', label: 'CV DEMO JAYA' },
    { value: 'PT TEST COMPANY', label: 'PT TEST COMPANY' },
    { value: 'PT EXAMPLE CORP', label: 'PT EXAMPLE CORP' },
  ];

  // Fetch drafter options
  const fetchDrafterOptions = async (ppjkNpwp: string) => {
    if (!ppjkNpwp) {
      console.log('No PPJK NPWP provided for drafter options');
      return;
    }

    // Use dummy data for demo users
    if (isDemoUser()) {
      console.log('Demo user detected, using dummy drafter data');
      setDrafterOptions(dummyDrafterOptions);
      return;
    }

    try {
      const response = await axiosInstance.get(`/api/v1/ceisa/uploads/drafter-names?ppjkNpwp=${ppjkNpwp}`);

      console.log('Drafter API response:', response.data);

      if (response.data?.success && response.data.data?.drafterNames) {
        const drafterOptions = response.data.data.drafterNames.map((name: string) => ({
          value: name,
          label: name,
        }));

        console.log('Setting drafter options:', drafterOptions);
        setDrafterOptions(drafterOptions);
      }
    } catch (error) {
      console.error('Error fetching drafter options:', error);
    }
  };

  // Fetch company options
  const fetchCompanyOptions = async (searchQuery = '', ppjkNpwpFilter = '') => {
    // Use dummy data for demo users
    if (isDemoUser()) {
      console.log('Demo user detected, using dummy company data');
      // If there's a search query, filter dummy data
      if (searchQuery) {
        const filteredOptions = dummyCompanyOptions.filter((option) =>
          option.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setCompanyOptions(filteredOptions);
      } else {
        setCompanyOptions(dummyCompanyOptions);
      }
      return;
    }

    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (ppjkNpwpFilter) params.append('ppjkNpwp', ppjkNpwpFilter);

      const response = await axiosInstance.get(`/api/v1/ceisa/uploads/customer-names?${params.toString()}`);

      if (response.data?.success && response.data.data?.customerNames) {
        const companyOptions = response.data.data.customerNames.map((name: string) => ({
          value: name,
          label: name,
        }));

        setCompanyOptions(companyOptions);
      }
    } catch (error) {
      console.error('Error fetching company options:', error);
    }
  };

  // Load initial data when modal opens
  useEffect(() => {
    if (opened && !ppjkLoading) {
      setLoading(true);

      // Fetch both options
      Promise.all([
        fetchCompanyOptions('', nomorPpjk || ''),
        nomorPpjk ? fetchDrafterOptions(nomorPpjk) : Promise.resolve(),
      ]).finally(() => {
        setLoading(false);
      });
    }
  }, [opened, nomorPpjk, ppjkLoading]);

  // Search handlers
  useEffect(() => {
    if (companySearchValue) {
      fetchCompanyOptions(companySearchValue, nomorPpjk || '');
    }
  }, [companySearchValue, nomorPpjk]);

  // Reset form when modal closes
  useEffect(() => {
    if (!opened) {
      setSelectedDrafter('');
      setSelectedCompany('');
      setCompanySearchValue('');
      setDrafterOptions([]);
      setCompanyOptions([]);
      setStartDate(null);
      setEndDate(null);
    }
  }, [opened]);

  const formatDateParam = (date: Date) => dayjs(date).format('DD-MM-YYYY');

  const appendDownloadParams = (params: URLSearchParams) => {
    if (selectedCompany) params.append('customerName', selectedCompany);
    if (selectedDrafter) params.append('drafterName', selectedDrafter);
    if (startDate) params.append('startDate', formatDateParam(startDate));
    if (endDate) params.append('endDate', formatDateParam(endDate));
    // Omitted when empty on purpose: the API reads an absent branch as the
    // caller's own, which is the same contract the listing uses.
    if (branchFilter) params.append('branch', branchFilter);
    params.append('limit', '500');
  };

  const hasDateRange = Boolean(startDate && endDate);
  const hasFilter = Boolean(selectedCompany || selectedDrafter || hasDateRange);

  const handleDownload = async () => {
    // Drafter is optional; require company, drafter, and/or date range
    if (!hasFilter) {
      toast.error(t('docpabean.errSelectCompanyDates'));
      return;
    }

    if (requiresDateRange && !hasDateRange) {
      toast.error(formatTemplate(t('docpabean.errDateRequiredCompany'), { company: DATE_REQUIRED_COMPANY }));
      return;
    }

    if (startDate && endDate && dayjs(endDate).isBefore(dayjs(startDate), 'day')) {
      toast.error(t('docpabean.errEndBeforeStart'));
      return;
    }

    setDownloadLoading(true);
    try {
      // For demo users, use dummy endpoint
      if (isDemoUser()) {
        console.log('Demo user download - calling dummy endpoint');

        const params = new URLSearchParams();
        appendDownloadParams(params);

        const response = await axiosInstance.get(
          `/api/v1/ceisa/uploads/dummy-export-history-status?${params.toString()}`,
          {
            responseType: 'blob', // Important for file downloads
          }
        );

        // Create blob and download
        const blob = new Blob([response.data]);
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;

        // Generate filename
        const filename = `demo_export_history_${selectedCompany ? `company_${selectedCompany}_` : ''}${selectedDrafter ? `drafter_${selectedDrafter}_` : ''}${new Date().toISOString().split('T')[0]}.xlsx`;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);

        toast.success(t('docpabean.successReportDownloaded'));
        onClose();
        return;
      }

      const params = new URLSearchParams();
      appendDownloadParams(params);

      const response = await axiosInstance.get(`/api/v1/ceisa/uploads/export-history-status?${params.toString()}`, {
        responseType: 'blob', // Important for file downloads
      });

      // Create blob and download
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Generate filename
      const filename = `export_history_${selectedCompany ? `company_${selectedCompany}_` : ''}${selectedDrafter ? `drafter_${selectedDrafter}_` : ''}${new Date().toISOString().split('T')[0]}.xlsx`;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(t('docpabean.successReportDownloaded'));
      onClose();
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error(t('docpabean.errReportDownload'));
    } finally {
      setDownloadLoading(false);
    }
  };

  const isValid = hasFilter && (!requiresDateRange || hasDateRange);

  const startId = useId();
  const endId = useId();
  const dateLabel = (id: string, text: string) => (
    <Label htmlFor={id}>
      {text}
      {requiresDateRange && <span className="text-err"> *</span>}
    </Label>
  );

  return (
    <Dialog open={opened} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download aria-hidden className="size-[18px]" />
            {t('docpabean.downloadModalTitle')}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="flex items-center justify-center gap-2 py-8 text-fg-muted text-sm">
            <Loader2 aria-hidden className="size-5 animate-spin text-label motion-reduce:animate-none" />
            {t('docpabean.loadingOptions')}
          </p>
        ) : (
          <div className="grid gap-5">
            <p className="text-fg-muted text-sm">
              {t('docpabean.filterHint')}
              {requiresDateRange
                ? ` ${formatTemplate(t('docpabean.errDateRequiredCompany'), { company: DATE_REQUIRED_COMPANY })}`
                : ''}
            </p>
            <div className="grid gap-4">
              <SearchSelect
                label={t('docpabean.drafterName')}
                placeholder={t('docpabean.drafterPlaceholder')}
                options={drafterOptions}
                value={selectedDrafter}
                onChange={setSelectedDrafter}
                emptyText={t('docpabean.noDrafters')}
                clearLabel={t('docpabean.drafterName')}
              />
              <SearchSelect
                label={t('docpabean.companyName')}
                placeholder={t('docpabean.companyPlaceholder')}
                options={companyOptions}
                value={selectedCompany}
                onChange={setSelectedCompany}
                searchValue={companySearchValue}
                onSearchChange={setCompanySearchValue}
                emptyText={t('docpabean.noCompanies')}
                clearLabel={t('docpabean.companyName')}
              />
              <div className="grid gap-1.5">
                {dateLabel(startId, t('docpabean.startDate'))}
                <DateField
                  id={startId}
                  placeholder={t('docpabean.selectStartDate')}
                  value={startDate}
                  onChange={setStartDate}
                  clearable
                  maxDate={endDate || undefined}
                />
              </div>
              <div className="grid gap-1.5">
                {dateLabel(endId, t('docpabean.endDate'))}
                <DateField
                  id={endId}
                  placeholder={t('docpabean.selectEndDate')}
                  value={endDate}
                  onChange={setEndDate}
                  clearable
                  minDate={startDate || undefined}
                />
              </div>
            </div>
          </div>
        )}

        {!loading && (
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={downloadLoading}>
              {t('docpabean.cancel')}
            </Button>
            <Button onClick={handleDownload} disabled={!isValid || downloadLoading}>
              {downloadLoading ? (
                <Loader2 aria-hidden className="animate-spin motion-reduce:animate-none" />
              ) : (
                <Download aria-hidden />
              )}
              {t('docpabean.downloadReport')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

type Option = { value: string; label: string };

/**
 * A searchable select whose field is its own search box, as Mantine's searchable Select was: typing filters the
 * options (label contains the text, any case), picking one shows its label in the field, × clears it, and leaving the
 * field puts the picked label back. With `searchValue` + `onSearchChange` the text is controlled, so the parent can
 * fetch on every keystroke (the company list does); a pick sets the text to the option's label, as Mantine did.
 */
function SearchSelect({
  label,
  placeholder,
  options,
  value,
  onChange,
  searchValue,
  onSearchChange,
  emptyText,
  clearLabel,
}: {
  label: string;
  placeholder: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  emptyText: string;
  clearLabel: string;
}) {
  const id = useId();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [ownSearch, setOwnSearch] = useState('');
  const search = searchValue ?? ownSearch;
  const setSearch = (text: string) => {
    if (onSearchChange) onSearchChange(text);
    else setOwnSearch(text);
  };
  const selected = options.find((o) => o.value === value);
  const selectedLabel = selected?.label ?? value;
  // While the field shows the picked label, the whole list stays visible (Mantine filtered only on typed text).
  const query = search.trim().toLowerCase();
  const shown =
    !query || search === selectedLabel ? options : options.filter((o) => o.label.toLowerCase().includes(query));

  const pick = (option: Option) => {
    // Picking the selected option again clears it (Mantine's allowDeselect).
    if (option.value === value) {
      onChange('');
      setSearch('');
    } else {
      onChange(option.value);
      setSearch(option.label);
    }
    setOpen(false);
  };

  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div className="relative">
            <input
              id={id}
              role="combobox"
              aria-expanded={open}
              aria-controls={listId}
              aria-autocomplete="list"
              autoComplete="off"
              placeholder={placeholder}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onClick={() => setOpen(true)}
              onBlur={() => {
                // Leaving the field puts the picked label back (or empties an unpicked search).
                if (search !== (value ? selectedLabel : '')) setSearch(value ? selectedLabel : '');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpen(false);
                if (e.key === 'Enter' && open && shown[0]) {
                  e.preventDefault();
                  pick(shown[0]);
                }
              }}
              className={cn(controlClasses, 'h-9 w-full px-3 py-1 pr-14 text-base md:text-sm')}
            />
            <span className="-translate-y-1/2 absolute top-1/2 right-2 flex items-center gap-0.5 text-fg-muted">
              {value && (
                <button
                  type="button"
                  aria-label={clearLabel}
                  className="grid size-6 place-items-center rounded-control hover:bg-tint hover:text-fg"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange('');
                    setSearch('');
                  }}
                >
                  <X aria-hidden className="size-3.5" />
                </button>
              )}
              <ChevronDown aria-hidden className="size-4" />
            </span>
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          className="w-(--radix-popover-trigger-width) p-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if ((e.target as HTMLElement | null)?.id === id) e.preventDefault();
          }}
        >
          <Command shouldFilter={false}>
            <CommandList id={listId} className="max-h-[200px]">
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {shown.map((option) => (
                  <CommandItem key={option.value} value={option.value} onSelect={() => pick(option)}>
                    <span className="flex-1">{option.label}</span>
                    {option.value === value && <Check aria-hidden className="size-4" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
